import { Star, ExternalLink } from "lucide-react";

const ZOOPLA_BRANCH_URL =
  "https://www.zoopla.co.uk/find-agents/branch/keystone-properties-batley-166551/";

/**
 * ZooplaStrip — CMS-driven "Rated on Zoopla" social-proof band that sits
 * under the homepage hero. Renders nothing unless `enabled` is true.
 *
 * The rating & review count are stored as strings so admins can leave them
 * blank while the strip is being introduced.
 */
const ZooplaStrip = ({ content }) => {
  if (!content?.enabled) return null;

  const ratingRaw = content.rating;
  const rating = ratingRaw !== "" && ratingRaw != null ? Number(ratingRaw) : null;
  const reviewCount = content.review_count;
  const eyebrow = content.eyebrow || "Trusted on Zoopla";
  const headline = content.headline || "See our verified reviews on Zoopla";
  const subtext = content.subtext || "";
  const ctaText = content.cta_text || "Read our Zoopla reviews";

  const hasRating = rating !== null && !Number.isNaN(rating) && rating > 0;
  const filled = hasRating ? Math.round(rating) : 0;

  return (
    <section
      className="bg-white border-b border-slate-200"
      data-testid="zoopla-strip"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8">
          {/* Zoopla badge */}
          <div className="flex items-center gap-3">
            <span
              className="inline-flex items-center justify-center rounded-md bg-[#4C2C79] text-white px-2.5 py-1 text-xs font-black tracking-wider leading-none"
              aria-hidden="true"
            >
              Zoopla
            </span>
            <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">
              {eyebrow}
            </span>
          </div>

          {/* Rating block */}
          {hasRating && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-0.5" data-testid="zoopla-strip-stars">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star
                    key={i}
                    className={`w-5 h-5 ${
                      i < filled
                        ? "text-[#4C2C79] fill-[#4C2C79]"
                        : "text-slate-300"
                    }`}
                  />
                ))}
              </div>
              <span
                className="text-navy font-heading font-bold text-lg leading-none"
                data-testid="zoopla-strip-rating"
              >
                {rating.toFixed(1)}
              </span>
              {reviewCount && (
                <span
                  className="text-slate-500 text-sm"
                  data-testid="zoopla-strip-count"
                >
                  · {reviewCount} reviews
                </span>
              )}
            </div>
          )}

          {/* Headline + subtext */}
          <div className="flex-1 min-w-0">
            <p className="text-navy font-medium leading-snug">{headline}</p>
            {subtext && (
              <p className="text-slate-500 text-sm mt-0.5">{subtext}</p>
            )}
          </div>

          {/* CTA */}
          <a
            href={ZOOPLA_BRANCH_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-[#4C2C79] hover:bg-[#3d2361] text-white px-5 py-2.5 text-sm font-semibold shadow-sm hover:shadow-md transition-all whitespace-nowrap"
            data-testid="zoopla-strip-cta"
          >
            {ctaText}
            <ExternalLink className="w-4 h-4 opacity-80" aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ZooplaStrip;
